import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useAppTranslation } from "../../../../../../../../../i18n/useAppTranslation";
import { Button } from "../../../../../../../../../shared/Button/Button";
import { svg } from "../../../../../../../../../assets/svg";
import { CircularProgress } from "../../../../../../../../../shared/CircularProgress/CircularProgress";
import { ReportHeader } from "../../../../../../../components/ReportHeader/ReportHeader";
import { DatePicker } from "../../../../../../../../../shared/DatePicker/DatePicker";
import { Select } from "../../../../../../../../../shared/Select/Select";
import styles from "./Header.module.scss";

export function Header({
  total,
  downloadExcel,
  isReportsLoading,
  isDownloadExcelDisabled,
  filter,
  onFilterChange,
  clinic,
}) {
  const { tCommon } = useAppTranslation.Common();
  const { tBi } = useAppTranslation.BusinessInsights();

  const setDays = (days) => {
    onFilterChange({ type: "days", days });
  };

  const setCustomRange = (from, to) => {
    onFilterChange({ type: "range", from, to });
  };

  const clinicOptions = useMemo(() => {
    const allClinicsOption = {
      label: tBi("shortTermLiabilityReport.filter.allClinics"),
      value: null,
    };
    const options = (clinic?.options || []).map((c) => ({
      label: c.name,
      value: c.id,
    }));
    return [allClinicsOption, ...options];
  }, [clinic?.options, tBi]);

  const selectedClinic = useMemo(() => {
    return (
      clinicOptions.find((option) => option.value === clinic?.value) ||
      clinicOptions[0]
    );
  }, [clinic?.value, clinicOptions]);

  return (
    <ReportHeader withBackBtn label={"Upcoming Expirations"} left={total}>
      <div className={styles.controls}>
        <div className={styles.quickButtons}>
          <Button
            size="small"
            variant={
              filter?.type === "days" && filter.days === 30
                ? "contained"
                : "outlined"
            }
            onClick={() => setDays(30)}
          >
            30d
          </Button>
          <Button
            size="small"
            variant={
              filter?.type === "days" && filter.days === 60
                ? "contained"
                : "outlined"
            }
            onClick={() => setDays(60)}
          >
            60d
          </Button>
          <Button
            size="small"
            variant={
              filter?.type === "days" && filter.days === 90
                ? "contained"
                : "outlined"
            }
            onClick={() => setDays(90)}
          >
            90d
          </Button>
          <Button
            size="small"
            variant={filter?.type === "range" ? "contained" : "outlined"}
            onClick={() =>
              onFilterChange({
                type: "range",
                from: undefined,
                to: undefined,
              })
            }
          >
            Custom
          </Button>
        </div>

        {filter?.type === "range" && (
          <div className={styles.datePickers}>
            <DatePicker
              className={styles.datePickerInput}
              selected={filter.from ? new Date(filter.from) : undefined}
              onChange={(d) =>
                setCustomRange(
                  d ? d.toISOString().split("T")[0] : undefined,
                  filter.to,
                )
              }
              placeholder="From"
            />
            <DatePicker
              className={styles.datePickerInput}
              selected={filter.to ? new Date(filter.to) : undefined}
              onChange={(d) =>
                setCustomRange(
                  filter.from,
                  d ? d.toISOString().split("T")[0] : undefined,
                )
              }
              placeholder="To"
            />
          </div>
        )}

        <Select
          size="small"
          className={styles.clinicsSelect}
          value={selectedClinic}
          onChange={(option) => clinic?.onChange(option?.value || null)}
          options={clinicOptions}
          isDisabled={isReportsLoading}
        />

        <Button
          size="small"
          variant="outlined"
          onClick={downloadExcel.initiate}
          isDisabled={
            downloadExcel.isLoading ||
            isReportsLoading ||
            isDownloadExcelDisabled
          }
          leftAdornment={
            downloadExcel.isLoading ? (
              <CircularProgress size="small" />
            ) : (
              <img src={svg.downloadPrimary} alt="dl" width="20px" />
            )
          }
        >
          {tCommon("label.downloadExcel")}
        </Button>
      </div>
    </ReportHeader>
  );
}

Header.propTypes = {
  downloadExcel: PropTypes.shape({
    initiate: PropTypes.func,
    isLoading: PropTypes.bool,
  }).isRequired,
  isReportsLoading: PropTypes.bool.isRequired,
  isDownloadExcelDisabled: PropTypes.bool.isRequired,
  filter: PropTypes.object,
  onFilterChange: PropTypes.func,
  clinic: PropTypes.shape({
    value: PropTypes.number,
    onChange: PropTypes.func,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      }),
    ),
  }),
};

Header.defaultProps = {
  filter: { type: "days", days: 30 },
  onFilterChange: () => {},
};
